import { useEffect, useMemo } from 'react';

import { useGetTicketList } from '@apis/rendall/queries/use-get-ticket-list';
import { useDedupeTickets } from '@packages/hooks/use-dedupe-tickets/use-dedupe-tickets';
import { useSelectedOrganization } from '@packages/legacy/organization-context';
import { TicketStatusOptions } from '@packages/legacy/ticket-status';
import { ticketKeys } from '@packages/utils/queryKeyFactories/tickets';

type UseFetchTicketsParams = {
  maxFetchedPages?: number; // if unset, will fetch all pages
  includeInactive?: boolean;
  after?: Date;
  before?: Date;
  statusFilter?: TicketStatusOptions | string;
};

const getIncludeInactiveFlag = (statusFilter = '') => {
  // Note - Following logic is based on https://arcticwolf.atlassian.net/browse/AWN-125295 ticket.
  if (
    statusFilter === TicketStatusOptions.Open ||
    statusFilter === TicketStatusOptions.WithCustomer ||
    statusFilter === TicketStatusOptions.WithAw
  ) {
    return false;
  }

  return true;
};

export const useListTickets = ({
  maxFetchedPages,
  includeInactive,
  after,
  before,
  statusFilter,
}: UseFetchTicketsParams = {}) => {
  const organization = useSelectedOrganization();

  const includeInactiveFlag =
    includeInactive ?? getIncludeInactiveFlag(statusFilter);

  const ticketListQueryKey = useMemo(
    () =>
      ticketKeys.list(organization.id, {
        after,
        before,
        includeInactive: includeInactiveFlag,
      }),
    [after, before, includeInactiveFlag, organization.id],
  );

  const {
    data,
    fetchNextPage,
    isFetching,
    isPending,
    isError,
    hasNextPage,
    error,
  } = useGetTicketList({ after, before, includeInactive: includeInactiveFlag });

  useEffect(() => {
    // Note - Following !isError condition is added for https://arcticwolf.atlassian.net/browse/AWN-126406 ticket.
    if (
      maxFetchedPages &&
      (data?.pages.length ?? 0) >= maxFetchedPages &&
      !isError
    ) {
      // max pages to fetch limit hit, stop fetching
      return;
    }

    // Note - Following !isError condition is added for https://arcticwolf.atlassian.net/browse/AWN-126406 ticket.
    if (!isFetching && hasNextPage && !isError) {
      fetchNextPage();
    }
  }, [data, isFetching, hasNextPage, fetchNextPage, maxFetchedPages, isError]);

  const dedupeTickets = useDedupeTickets(data?.pages);

  const done = !isFetching && !hasNextPage;

  return {
    done,
    error,
    fetched: dedupeTickets,
    isError,
    isFetching,
    isPending,
    ticketListQueryKey,
  };
};
